import { makeStyles, shorthands } from "@fluentui/react-components";
import designTokens from "theme/customTheme";

const pulseKeyframes = {
  "0%": { opacity: 1 },
  "50%": { opacity: 0.4 },
  "100%": { opacity: 1 },
};

export const useQuickStartGalleryStyles = makeStyles({
  galleryButton: {
    padding: designTokens.spacing[3],
    backgroundColor: designTokens.neutral[100],
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    borderRadius: designTokens.borderRadius.lg,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ":hover": {
      backgroundColor: designTokens.accent[100],
      ...shorthands.borderColor(designTokens.accent[400]),
    },
  },

  galleryButtonIcon: {
    width: designTokens.spacing[5],
    height: designTokens.spacing[5],
    color: designTokens.neutral[600],
  },

  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    opacity: 0,
    visibility: "hidden",
    transition: `all ${designTokens.transition.fast}`,
  },

  modalOverlayActive: {
    opacity: 1,
    visibility: "visible",
  },

  modal: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: designTokens.neutral.white,
    borderRadius: designTokens.borderRadius.xl,
    width: "90%",
    maxWidth: "900px",
    height: "80%",
    maxHeight: "600px",
    ...shorthands.overflow("hidden"),
    display: "flex",
    flexDirection: "column",
    boxShadow: designTokens.shadow.xl,
    transform: "scale(0.95)",
    transition: `transform ${designTokens.transition.fast}`,
  },

  modalActive: {
    transform: "scale(1)",
  },

  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[6]}`,
    ...shorthands.borderBottom("1px", "solid", designTokens.neutral[200]),
  },

  modalTitle: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.lg,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    margin: 0,
  },

  modalDescription: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    margin: 0,
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[6]} ${designTokens.spacing[2]}`,
    lineHeight: designTokens.lineHeight.normal,
  },

  closeButton: {
    width: designTokens.controlHeight.md,
    height: designTokens.controlHeight.md,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    color: designTokens.neutral[500],
    transition: `all ${designTokens.transition.fast}`,
    ":hover": {
      backgroundColor: designTokens.neutral[100],
      color: designTokens.neutral[700],
    },
  },

  modalBody: {
    display: "flex",
    flex: 1,
    ...shorthands.overflow("hidden"),
  },

  templateList: {
    width: "280px",
    ...shorthands.borderRight("1px", "solid", designTokens.neutral[200]),
    display: "flex",
    flexDirection: "column",
    ...shorthands.overflow("hidden"),
  },

  templateListHeader: {
    fontFamily: designTokens.fontFamily,
    padding: `${designTokens.spacing[3]} ${designTokens.spacing[4]}`,
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    fontWeight: designTokens.fontWeight.medium,
  },

  templateListItems: {
    flex: 1,
    overflowY: "auto",
    padding: `0 ${designTokens.spacing[2]}`,
  },

  templateListItem: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[3]),
    padding: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
    cursor: "pointer",
    transition: `all ${designTokens.transition.fast}`,
    marginBottom: "2px",
    ":hover": {
      backgroundColor: designTokens.neutral[50],
    },
  },

  templateListItemActive: {
    backgroundColor: designTokens.accent[50],
  },

  templateListItemIcon: {
    width: designTokens.spacing[5],
    height: designTokens.spacing[5],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: designTokens.fontSize.sm,
  },

  templateListItemName: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[700],
  },

  templateListItemNameActive: {
    color: designTokens.accent[700],
    fontWeight: designTokens.fontWeight.medium,
  },

  templateDetail: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: `0 ${designTokens.spacing[6]} ${designTokens.spacing[6]}`,
    overflowY: "auto",
  },

  templateDetailName: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize["2xl"],
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[900],
    marginBottom: designTokens.spacing[4],
  },

  templateDetailSection: {
    marginBottom: designTokens.spacing[5],
  },

  templateDetailLabel: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[500],
    letterSpacing: "0.02em",
    marginBottom: designTokens.spacing[2],
  },

  templateDetailOverview: {
    fontFamily: designTokens.fontFamily,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    lineHeight: designTokens.lineHeight.relaxed,
  },

  knowledgeList: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap(designTokens.spacing[2]),
  },

  knowledgeTag: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: designTokens.neutral[100],
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.full,
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[700],
  },

  llmTag: {
    fontFamily: designTokens.fontFamily,
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[2]),
    backgroundColor: designTokens.accent[50],
    ...shorthands.border("1px", "solid", designTokens.accent[200]),
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.accent[700],
  },

  llmTagIcon: {
    width: "16px",
    height: "16px",
  },

  modalFooter: {
    padding: `${designTokens.spacing[4]} ${designTokens.spacing[6]}`,
    ...shorthands.borderTop("1px", "solid", designTokens.neutral[200]),
    display: "flex",
    justifyContent: "flex-end",
  },

  useTemplateButton: {
    fontFamily: designTokens.fontFamily,
    backgroundColor: designTokens.accent[500],
    color: "#ffffff",
    ...shorthands.border("none"),
    padding: `${designTokens.spacing[2]} ${designTokens.spacing[6]}`,
    borderRadius: designTokens.borderRadius.lg,
    fontSize: designTokens.fontSize.sm,
    cursor: "pointer",
    transition: `background ${designTokens.transition.fast}`,
    lineHeight: designTokens.lineHeight.normal,
    ":hover": {
      backgroundColor: designTokens.accent[600],
    },
    ":active": {
      backgroundColor: designTokens.accent[700],
    },
    ":disabled": {
      backgroundColor: designTokens.neutral[300],
      cursor: "not-allowed",
      opacity: 0.6,
    },
  },

  skeletonLoader: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(designTokens.spacing[3]),
    padding: designTokens.spacing[4],
  },

  skeletonLine: {
    height: designTokens.spacing[4],
    backgroundColor: designTokens.neutral[200],
    borderRadius: designTokens.borderRadius.sm,
    animationName: pulseKeyframes,
    animationDuration: "1.5s",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
  },

  skeletonListItem: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(designTokens.spacing[3]),
    padding: designTokens.spacing[3],
    marginBottom: designTokens.spacing[1],
  },

  skeletonIcon: {
    width: designTokens.spacing[5],
    height: designTokens.spacing[5],
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: designTokens.neutral[200],
    flexShrink: 0,
  },

  skeletonText: {
    flex: 1,
    height: designTokens.fontSize.sm,
    backgroundColor: designTokens.neutral[200],
    borderRadius: designTokens.borderRadius.sm,
  },

  skeletonTitle: {
    width: "60%",
    height: designTokens.spacing[8],
  },

  skeletonLine1: {
    width: "100%",
    marginTop: designTokens.spacing[4],
  },

  skeletonLine2: {
    width: "95%",
  },

  skeletonLine3: {
    width: "90%",
  },

  skeletonMeta: {
    width: "40%",
    marginTop: designTokens.spacing[6],
  },

  skeletonButton: {
    width: "30%",
    height: designTokens.spacing[8],
  },
});
